import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PaperPlaneTilt, ArrowsClockwise, CheckCircle, XCircle, ClockCountdown } from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const STATUS_BADGE = {
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-600",
  skipped_no_provider: "bg-amber-50 text-amber-700",
  skipped_test_address: "bg-ink-100 text-ink-500",
};

export default function AdminEmails() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/welcome-emails/summary"),
        api.get("/admin/welcome-emails/records"),
      ]);
      setSummary(s.data);
      setRecords(r.data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendAll = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const { data } = await api.post("/admin/welcome-emails/send-all");
      if (data.started) {
        toast.success(`Sending started to ${data.recipients} recipients`);
        setTimeout(load, 4000);
      } else {
        toast.info(data.detail || "No eligible recipients");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start sending");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-emails-page">
      <div className="overline mb-2">{t("admin.emails", "Emails")}</div>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tightest">
          {t("admin.emailCampaigns", "Customer emails")}
        </h1>
        <button onClick={load} disabled={loading}
          className="btn-ghost text-xs font-semibold flex items-center gap-1.5 text-cobalt-600 hover:bg-cobalt-50 disabled:opacity-50"
          data-testid="emails-refresh">
          <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          {t("common.refresh", "Refresh")}
        </button>
      </div>

      {loadError && (
        <div className="card-soft p-6 mb-8 text-sm text-destructive" data-testid="emails-load-error">
          Could not load email data. <button onClick={load} className="underline font-semibold">Retry</button>
        </div>
      )}

      {summary && (
        <div className="card-soft p-4 sm:p-6 mb-8" data-testid="welcome-email-card">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h3 className="font-display font-bold text-lg tracking-tight">Welcome email — "Welcome to BlueRoam 🌍"</h3>
              <p className="text-xs text-ink-500 mt-1 max-w-full sm:max-w-lg">
                Sent automatically to every new registration. The button sends it once to all existing customers —
                already-sent users and test accounts are excluded, so duplicates are impossible.
              </p>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 max-w-full sm:max-w-md">
                <div className="bg-cobalt-50 rounded-2xl p-3 text-center">
                  <div className="font-display font-black text-2xl" data-testid="welcome-eligible">{summary.eligible_recipients}</div>
                  <div className="text-[11px] text-ink-500 font-semibold uppercase tracking-wider">Eligible</div>
                </div>
                <div className="bg-emerald-50 rounded-2xl p-3 text-center">
                  <div className="font-display font-black text-2xl">{summary.already_sent}</div>
                  <div className="text-[11px] text-ink-500 font-semibold uppercase tracking-wider">Sent</div>
                </div>
                <div className="bg-red-50 rounded-2xl p-3 text-center">
                  <div className="font-display font-black text-2xl">{summary.failed}</div>
                  <div className="text-[11px] text-ink-500 font-semibold uppercase tracking-wider">Failed</div>
                </div>
              </div>
              <div className="text-xs text-ink-500 mt-3 break-words">
                Sender: <b>{summary.sender}</b> · Provider: {summary.provider}
              </div>
            </div>
            <button onClick={() => setConfirmOpen(true)} disabled={sending || summary.eligible_recipients === 0}
              className="btn-primary text-sm disabled:opacity-50 flex items-center justify-center gap-2 w-full sm:w-auto flex-shrink-0"
              data-testid="welcome-send-all">
              <PaperPlaneTilt size={16} weight="bold" />
              {sending ? "Starting…" : `Send to ${summary.eligible_recipients} customers`}
            </button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="welcome-send-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send welcome email to {summary?.eligible_recipients} customers?</AlertDialogTitle>
            <AlertDialogDescription>
              Emails will be sent from {summary?.sender} via {summary?.provider}. Users who already received it
              are skipped automatically — duplicates are impossible. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="welcome-send-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendAll} data-testid="welcome-send-confirm">Send now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <h2 className="font-display font-bold text-lg tracking-tight mb-3">Delivery log</h2>
      <div className="card-soft overflow-x-auto !rounded-2xl">
        <table className="w-full text-sm" data-testid="welcome-records-table">
          <thead className="bg-cobalt-50 text-xs uppercase tracking-widest text-cobalt-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Recipient</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Message ID</th>
              <th className="text-right px-4 py-3 font-semibold">Attempts</th>
              <th className="text-left px-4 py-3 font-semibold">Sent at</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.user_id} className="border-t border-ink-100 hover:bg-sky-soft/40">
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[r.status] || "bg-ink-100 text-ink-500"}`}>
                    {r.status === "sent" ? <CheckCircle size={12} weight="fill" /> : r.status === "failed" ? <XCircle size={12} weight="fill" /> : <ClockCountdown size={12} />}
                    {r.status.replace(/_/g, " ")}
                  </span>
                  {r.error && <div title={r.error} className="text-[10px] text-red-500 mt-1 max-w-[240px] truncate">{r.error}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-500">{r.resend_id || "—"}</td>
                <td className="px-4 py-3 text-right">{r.attempts}</td>
                <td className="px-4 py-3 text-xs text-ink-500">{(r.sent_at || r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {records.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-ink-500" data-testid="welcome-records-empty">No welcome emails sent yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
